/**
 * OnlineContext — one source of truth for network state, SW lifecycle,
 * pending-sync queue size, and install-prompt availability.
 *
 * Components consume `useOnlineStatus()` for a lightweight boolean,
 * or `useOnline()` for the full state including sync counts.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Workbox } from 'workbox-window';
import { listQueued, dequeueMutation, markAttempt, queueSize, moveToFailed, failedCount } from '../lib/offline';
import { apiBase } from '../lib/absoluteMediaUrl';
import axios from 'axios';

const API_URL = apiBase();
const OnlineContext = createContext(null);

/* Direct axios calls for sync flush — bypass the regular `api` instance's
 * 401 refresh interceptor because we want raw network semantics here. */
const syncClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 20000,
});

export function OnlineProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedSyncCount, setFailedSyncCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const syncingRef = useRef(false);

  /* Refresh queue size — called after enqueues and after flushes */
  const refreshQueueSize = useCallback(async () => {
    try {
      const [n, fc] = await Promise.all([queueSize(), failedCount()]);
      setPendingCount(n);
      setFailedSyncCount(fc);
    } catch (e) { console.warn('[OnlineContext] refreshQueueSize failed', e); }
  }, []);

  /* Flush the sync queue in FIFO order.
   * Methodology hold #3: user data is portable — on exhaustion we MOVE
   * to failed_queue (not delete). Users can export the full bundle including
   * failed mutations so no work is ever silently dropped. */
  const flushQueue = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      const queue = await listQueued();
      for (const m of queue) {
        try {
          await syncClient.request({
            method: m.method,
            url: m.path,
            data: m.body,
          });
          await dequeueMutation(m.id);
          // eslint-disable-next-line no-console
          console.log('[TSOGN sync] ✓ replayed', m.kind || m.method, m.path);
        } catch (err) {
          const status = err?.response?.status;
          const isClientErr = status && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
          // eslint-disable-next-line no-console
          console.warn('[TSOGN sync] ✗ replay failed', m.kind || m.method, m.path, 'status:', status, 'detail:', err?.response?.data?.detail || err?.message);
          if (isClientErr) {
            if ((m.attempts || 0) >= 2) {
              // Preserve — never silently drop user work
              await moveToFailed(m, `${status}: ${err?.response?.data?.detail || 'client error'}`);
              continue;
            }
          }
          await markAttempt(m.id);
          // 5xx / network → stop and retry later; don't keep hammering
          break;
        }
      }
    } finally {
      syncingRef.current = false;
      setSyncing(false);
      refreshQueueSize();
    }
  }, [refreshQueueSize]);

  /* ─ Listen to online/offline ─ */
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      flushQueue();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    // Initial
    refreshQueueSize();
    if (navigator.onLine) flushQueue();
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [flushQueue, refreshQueueSize]);

  /* ─ Periodic queue-size refresh so UI stays honest ─ */
  useEffect(() => {
    const t = setInterval(refreshQueueSize, 5000);
    return () => clearInterval(t);
  }, [refreshQueueSize]);

  /* ─ Service Worker registration via Workbox ─ */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const wb = new Workbox('/sw.js');
    wb.addEventListener('waiting', () => setUpdateReady(true));
    wb.register().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[TSOGN] SW register failed:', err);
    });
  }, []);

  /* ─ PWA install prompt (Chrome/Edge) ─ */
  useEffect(() => {
    const capture = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', capture);
    return () => window.removeEventListener('beforeinstallprompt', capture);
  }, []);

  const triggerInstall = useCallback(async () => {
    if (!installPrompt) return { outcome: 'unavailable' };
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    return choice;
  }, [installPrompt]);

  const applyUpdate = useCallback(() => {
    // Tell waiting SW to activate and reload once it's in control
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      }, { once: true });
    }
    // Message waiting SW to skip
    navigator.serviceWorker?.getRegistration()?.then((r) => {
      r?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    });
  }, []);

  return (
    <OnlineContext.Provider
      value={{
        online,
        pendingCount,
        failedSyncCount,
        syncing,
        flushQueue,
        refreshQueueSize,
        installable: !!installPrompt,
        triggerInstall,
        updateReady,
        applyUpdate,
      }}
    >
      {children}
    </OnlineContext.Provider>
  );
}

export function useOnline() {
  const ctx = useContext(OnlineContext);
  if (!ctx) {
    // Degrade gracefully if provider missing
    return { online: true, pendingCount: 0, failedSyncCount: 0, syncing: false, flushQueue: () => {}, refreshQueueSize: () => {}, installable: false, triggerInstall: () => {}, updateReady: false, applyUpdate: () => {} };
  }
  return ctx;
}

export function useOnlineStatus() {
  return useOnline().online;
}
